import { getToken } from '../context/AuthContext';

const FULL_URL = process.env.REACT_APP_API_BASE;
const BASE_URL = process.env.NODE_ENV === 'development'
  ? new URL(FULL_URL).pathname
  : FULL_URL;
const PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

let cachedOfficers = null;
let metersCache = null;
let metersCacheAt = 0;
let metersInFlight = null;
let dofficersCache = null;
let dofficersCacheAt = 0;
let dofficersInFlight = null;

const METERS_CACHE_MS = 2 * 60 * 1000;
const DOFFICERS_CACHE_MS = 2 * 60 * 1000;
const METERS_PAGE_SIZE = 100;

/**
 * Error thrown by the API layer. Carries enough context (HTTP status, original
 * cause) to let UI code branch on category without parsing strings, while the
 * `message` itself is always safe to display directly to a user.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, code, original } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.original = original;
  }
}

const SERVER_ERROR_MESSAGE =
  'The server is temporarily unavailable. Please try again in a moment.';
const NETWORK_ERROR_MESSAGE =
  'Unable to reach the server. Check your internet connection and try again.';
const TIMEOUT_ERROR_MESSAGE =
  'The server took too long to respond. Please try again.';
const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please log in again.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Returns a user-safe message for any error thrown by the API layer or thrown
 * locally during form validation. Technical details (stack traces, raw HTTP
 * status text, `TypeError: Failed to fetch`, etc.) are mapped to friendly
 * sentences; the original error is still logged to the console for debugging.
 */
export function humanizeError(err, fallback = GENERIC_ERROR_MESSAGE) {
  if (err) {
    // Always keep the technical detail in devtools for debugging.
    // eslint-disable-next-line no-console
    console.error('[ui] surfaced error:', err);
  }
  if (!err) return fallback;

  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return SESSION_EXPIRED_MESSAGE;
    if (err.status === 408 || err.status === 504) return TIMEOUT_ERROR_MESSAGE;
    if (err.status === 0) return NETWORK_ERROR_MESSAGE;
    // 4xx/5xx → prefer the curated message from ApiError when present.
    if (err.status >= 500 && err.message && err.message !== SERVER_ERROR_MESSAGE) {
      return err.message;
    }
    if (err.status >= 500) return SERVER_ERROR_MESSAGE;
    return err.message || fallback;
  }

  const rawMessage = typeof err.message === 'string' ? err.message : '';

  // Browser network/CORS failures look like `TypeError: Failed to fetch`.
  if (
    err.name === 'TypeError' ||
    /failed to fetch|network ?error|networkerror/i.test(rawMessage)
  ) {
    return NETWORK_ERROR_MESSAGE;
  }

  // Strip out raw `API 5xx: ...` strings that older call sites may still throw.
  if (/^API\s+5\d\d/i.test(rawMessage)) return SERVER_ERROR_MESSAGE;
  if (/^API\s+\d{3}/i.test(rawMessage)) return fallback;

  return rawMessage || fallback;
}

/** Map legacy backend/auth duplicate messages to a clear email-specific message. */
export function normalizeUserDuplicateMessage(message) {
  if (!message || typeof message !== 'string') return message;
  const lower = message.toLowerCase();
  if (
    lower === 'user already exists' ||
    (lower.includes('user already exists') && !lower.includes('email is already')) ||
    (lower.includes('auth sync failed') &&
      (lower.includes('user already exists') || lower.includes('email already')))
  ) {
    return 'Email is already registered. Please use a different email.';
  }
  return message;
}

/** Extract human-readable message from canonical API error/success envelopes. */
export function getApiErrorMessage(result, fallback = 'Request failed') {
  const errObj = result?.error && typeof result.error === 'object' ? result.error : null;
  const fieldMsg =
    Array.isArray(errObj?.fieldErrors) &&
    errObj.fieldErrors.find((fe) => typeof fe?.message === 'string')?.message;
  const raw =
    (errObj && typeof errObj.message === 'string' && errObj.message) ||
    (typeof fieldMsg === 'string' && fieldMsg) ||
    (typeof result?.message === 'string' && result.message) ||
    (typeof result?.error === 'string' && result.error) ||
    fallback;
  return normalizeUserDuplicateMessage(raw);
}

/**
 * Inspect a non-ok `fetch` Response, parse its body, and throw an `ApiError`
 * with a safe, user-facing message:
 *   - 401/403 → session-expired (caller usually redirects beforehand)
 *   - 408/504 → timeout sentinel
 *   - >=500   → generic "server is temporarily unavailable" (raw backend
 *              messages, stack traces, etc. are intentionally discarded)
 *   - 4xx     → curated backend message if present, else `fallback4xx`
 *
 * Use this from any direct `fetch` site that doesn't go through `apiFetch`.
 */
async function throwForResponse(response, fallback4xx = 'Request failed. Please try again.') {
  const body = await response.json().catch(() => null);
  const status = response.status;

  if (status === 401 || status === 403) {
    throw new ApiError(SESSION_EXPIRED_MESSAGE, { status });
  }
  if (status === 408 || status === 504) {
    throw new ApiError(TIMEOUT_ERROR_MESSAGE, { status });
  }
  if (status >= 500) {
    const message = body ? getApiErrorMessage(body, SERVER_ERROR_MESSAGE) : SERVER_ERROR_MESSAGE;
    throw new ApiError(message, { status });
  }

  const message = body ? getApiErrorMessage(body, fallback4xx) : fallback4xx;
  throw new ApiError(message, { status });
}

export function invalidateMetersCache() {
  metersCache = null;
  metersCacheAt = 0;
  metersInFlight = null;
  dofficersCache = null;
  dofficersCacheAt = 0;
  dofficersInFlight = null;
}

async function apiFetch(endpoint, params = {}) {
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const url = `${BASE_URL}${endpoint}${query ? '?' + query : ''}`;

  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(url, { headers });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  if (res.status === 401 || res.status === 403) {
    sessionStorage.removeItem('bi_token');
    sessionStorage.removeItem('bi_user');
    window.location.href = '/login';
    throw new ApiError(SESSION_EXPIRED_MESSAGE, { status: res.status });
  }

  if (!res.ok) {
    // Delegate to the shared helper so every non-ok response (5xx masked,
    // 4xx with curated + normalized backend message, 408/504 timeout) is
    // turned into a uniform `ApiError` ready for `humanizeError`.
    await throwForResponse(res, 'Request failed. Please try again.');
  }

  return res.json();
}

export async function fetchUsers() {
  const result = await apiFetch('/users');
  return result.data || [];
}

export async function fetchRoles(page = 1, pageSize = 100) {
  const result = await apiFetch('/roles', {
    project_id: PROJECT_ID,
    page,
    pageSize,
  });
  return result.data || [];
}

export function isFieldOfficerRole(role) {
  if (!role) return false;
  const code = String(role.code || '').toUpperCase();
  const name = String(role.name || '').toLowerCase();
  return code === 'FIELD_OFFICER' || code === 'FO' || name.includes('field officer');
}

let cachedFieldOfficerRoleId = null;

export function isFieldOfficerUser(user) {
  if (!user) return false;
  if (isFieldOfficerRole(user.role)) return true;
  const code = String(user.role_code || '').toUpperCase();
  const name = String(user.role_name || '').toLowerCase();
  if (code === 'FIELD_OFFICER' || code === 'FO' || name.includes('field officer')) {
    return true;
  }
  const roleId = Number(user.role_id ?? user.role?.id);
  if (cachedFieldOfficerRoleId != null && roleId === cachedFieldOfficerRoleId) {
    return true;
  }
  return false;
}

function extractCreatedUser(json) {
  if (!json || typeof json !== 'object') return null;
  if (json.data && typeof json.data === 'object') return json.data;
  if (json.user && typeof json.user === 'object') return json.user;
  if (json.id != null) return json;
  return null;
}

function assertCreateUserSuccess(json, fallbackMessage) {
  if (!json || json.status === 'error' || json.success === false) {
    throw new ApiError(getApiErrorMessage(json, fallbackMessage), { status: 400 });
  }
  const created = extractCreatedUser(json);
  if (created?.id == null) {
    throw new ApiError(
      'The server did not confirm the new user. Check the Users list or try again.',
      { status: 500 },
    );
  }
  return { ...json, data: created };
}

export function mapUserToFieldOfficerRow(user) {
  if (!user) return null;
  const active = user.is_active !== false;
  return {
    id: user.id,
    name:
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
      user.username ||
      '—',
    contact: user.phone || user.alternate_phone || '—',
    installed: 0,
    accepted: 0,
    rejected: 0,
    status: active ? 'Active' : 'Inactive',
    roleId: user.role_id ?? user.role?.id,
    username: user.username || '—',
    state: '—',
    depot: '—',
    area: '—',
    qtr: '—',
  };
}

export async function fetchAllRoles({ projectScoped = true } = {}) {
  const pageSize = 100;
  let page = 1;
  const all = [];

  while (true) {
    const params = { page, pageSize };
    if (projectScoped && PROJECT_ID != null && PROJECT_ID !== '') {
      params.project_id = PROJECT_ID;
    }
    const result = await apiFetch('/roles', params);
    const batch = Array.isArray(result?.data) ? result.data : [];
    all.push(...batch);

    if (batch.length < pageSize) break;
    page += 1;
  }

  return all;
}

async function fetchAllRolesIncludingGlobal() {
  const [scoped, global] = await Promise.all([
    fetchAllRoles({ projectScoped: true }),
    fetchAllRoles({ projectScoped: false }),
  ]);
  const byId = new Map();
  [...global, ...scoped].forEach((role) => {
    if (role?.id != null) byId.set(Number(role.id), role);
  });
  return [...byId.values()];
}

export async function getFieldOfficerRoleId() {
  if (cachedFieldOfficerRoleId != null) return cachedFieldOfficerRoleId;
  const roles = await fetchAllRolesIncludingGlobal();
  const foRole = roles.find((role) => isFieldOfficerRole(role));
  const id = foRole?.id ? Number(foRole.id) : null;
  if (id != null) cachedFieldOfficerRoleId = id;
  return id;
}

export async function fetchUsersForUsersPage() {
  const [users, roles] = await Promise.all([fetchAllUsers(), fetchAllRolesIncludingGlobal()]);

  const relevantRoleIds = new Set(
    roles
      .filter((role) => {
        const code = String(role?.code || '').toUpperCase();
        const name = String(role?.name || '').toLowerCase();
        return (
          isFieldOfficerRole(role) ||
          code.includes('ADMIN') ||
          name.includes('admin')
        );
      })
      .map((role) => Number(role.id))
      .filter(Boolean),
  );

  return users.filter((user) => {
    const roleCode = String(user?.role?.code || '').toUpperCase();
    const roleName = String(user?.role?.name || '').toLowerCase();
    const roleId = Number(user?.role_id || user?.role?.id);

    return (
      relevantRoleIds.has(roleId) ||
      isFieldOfficerUser(user) ||
      roleCode.includes('ADMIN') ||
      roleName.includes('admin')
    );
  });
}

function handleAuthExpiry(response) {
  if (response.status === 401 || response.status === 403) {
    sessionStorage.removeItem('bi_token');
    sessionStorage.removeItem('bi_user');
    window.location.href = '/login';
    throw new ApiError(SESSION_EXPIRED_MESSAGE, { status: response.status });
  }
}

export async function createUser(payload) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${BASE_URL}/users`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to create user. Please try again.');
  }
  const json = await response.json().catch(() => ({}));
  clearOfficerCache();
  return assertCreateUserSuccess(json, 'Failed to create user. Please try again.');
}

export async function createFieldOfficer(payload) {
  const roleId = await getFieldOfficerRoleId();
  if (!roleId) {
    throw new ApiError(
      'Field Officer role (FO) was not found. Ask an administrator to configure roles in the system.',
      { status: 400 },
    );
  }

  const projectIds = payload?.project_ids;
  if (!projectIds?.length) {
    throw new ApiError('Select at least one project to assign.', { status: 400 });
  }

  return createUser({
    ...payload,
    role_id: roleId,
  });
}

export async function updateUser(userId, payload) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${BASE_URL}/users/${encodeURIComponent(String(userId))}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to update user. Please try again.');
  }
  return response.json().catch(() => ({}));
}

export async function setUserActive(userId, isActive) {
  const result = await updateUser(userId, { is_active: Boolean(isActive) });
  clearOfficerCache();
  return result;
}

export async function deleteUser(userId) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${BASE_URL}/users/${encodeURIComponent(String(userId))}`, {
      method: 'DELETE',
      headers,
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (response.ok) return {};
  await throwForResponse(response, 'Failed to delete user. Please try again.');
}

export async function fetchAllUsers() {
  const pageSize = 100;
  let page = 1;
  const all = [];

  while (true) {
    const result = await apiFetch('/users', { page, pageSize });
    const batch = result.data || [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    page += 1;
  }

  return all;
}

export async function fetchFieldOfficers() {
  if (cachedOfficers) return cachedOfficers;
  const users = await fetchUsers();
  const officers = {};
  users.forEach((u) => {
    officers[u.id] = {
      id: u.id,
      username: u.username,
      firstName: u.first_name,
      lastName: u.last_name,
      phone: u.phone,
      email: u.email,
      role: u.role?.code,
      roleName: u.role?.name,
      isActive: u.is_active,
    };
  });
  cachedOfficers = officers;
  return officers;
}

function mapFieldOfficerRow(fo) {
  const hierarchy = Array.isArray(fo.hierarchy) ? fo.hierarchy : [];
  const byLevel = (level) =>
    hierarchy.find((h) => Number(h.level) === level)?.name ?? '—';

  const active =
    fo.is_active !== false &&
    String(fo.status || '').toUpperCase() !== 'INACTIVE' &&
    String(fo.status || '').toUpperCase() !== 'LOCKED';

  return {
    id: fo.id,
    name:
      [fo.first_name, fo.last_name].filter(Boolean).join(' ').trim() ||
      fo.username ||
      '—',
    contact: fo.phone || fo.alternate_phone || '—',
    installed: Number(fo.installed) || 0,
    accepted: Number(fo.accepted) || 0,
    rejected: Number(fo.rejected) || 0,
    status: active ? 'Active' : 'Inactive',
    roleId: fo.role?.id ?? fo.role_id,
    username: fo.username || '—',
    state: byLevel(1),
    depot: byLevel(2),
    area: byLevel(3),
    qtr: byLevel(4),
  };
}

async function fetchAllUsersPaged() {
  const pageSize = 100;
  let page = 1;
  const allUsers = [];
  while (true) {
    const result = await apiFetch('/users', { page, pageSize });
    const batch = Array.isArray(result?.data) ? result.data : [];
    allUsers.push(...batch);
    if (batch.length < pageSize) break;
    page += 1;
  }
  return allUsers;
}

async function fetchFieldOfficersFromApi(projectId) {
  const listParams = {};
  if (projectId != null && projectId !== '') {
    listParams.project_id = projectId;
  }

  const pageSize = 100;
  let page = 1;
  const allOfficers = [];
  while (true) {
    const result = await apiFetch('/users/field-officers', {
      ...listParams,
      page,
      limit: pageSize,
    });
    const batch = Array.isArray(result?.data) ? result.data : [];
    allOfficers.push(...batch);
    const meta = result?.meta?.pagination;
    if (meta?.hasNextPage === true) {
      page += 1;
      continue;
    }
    if (batch.length < pageSize) break;
    page += 1;
  }
  return allOfficers;
}

export async function fetchDofficers({ force = false, projectId } = {}) {
  const now = Date.now();
  if (!force && dofficersCache && now - dofficersCacheAt < DOFFICERS_CACHE_MS) {
    return dofficersCache;
  }
  if (!force && dofficersInFlight) return dofficersInFlight;

  const scopedProjectId =
    projectId !== undefined && projectId !== null && projectId !== ''
      ? projectId
      : null;

  dofficersInFlight = (async () => {
    try {
      await getFieldOfficerRoleId();

      let allOfficers = [];
      try {
        allOfficers = await fetchFieldOfficersFromApi(scopedProjectId);
      } catch (apiErr) {
        if (apiErr?.status !== 404 && apiErr?.status !== 405) {
          throw apiErr;
        }
      }

      if (allOfficers.length === 0) {
        const allUsers = await fetchAllUsersPaged();
        let officers = allUsers.filter((user) => isFieldOfficerUser(user));
        if (scopedProjectId != null) {
          const pid = Number(scopedProjectId);
          if (Number.isFinite(pid)) {
            officers = officers.filter((user) =>
              (user.user_projects || []).some((up) => Number(up.project_id) === pid),
            );
          }
        }
        allOfficers = officers;
      }

      const result = allOfficers.map(mapFieldOfficerRow);

      dofficersCache = result;
      dofficersCacheAt = Date.now();
      return result;
    } finally {
      dofficersInFlight = null;
    }
  })();

  return dofficersInFlight;
}

export function getOfficerName(officers, userId) {
  const officer = officers[userId];
  if (!officer) return '—';
  return officer.username;
}

export function getActiveFieldOfficers(officers) {
  return Object.values(officers).filter(
    (o) => isFieldOfficerRole({ code: o.role, name: o.roleName }) && o.isActive,
  );
}

function metersStatusQuery(status) {
  if (!status || status === 'All') return 'pending,accepted,approved,rejected';
  const s = String(status).toLowerCase();
  // Installation review sets APPROVED; legacy rows may use ACCEPTED
  if (s === 'accepted') return 'accepted,approved';
  return s;
}

export async function fetchMeterWidgets(projectId = PROJECT_ID) {
  const result = await apiFetch('/meters/widgets', {
    projectId: projectId ?? undefined,
  });
  return result.data || {};
}

export async function fetchMeterMonthlyReport({
  range = 'Year',
  projectId = PROJECT_ID,
  fieldOfficerId,
} = {}) {
  const result = await apiFetch('/meters/monthly-report', {
    range,
    projectId: projectId ?? undefined,
    fieldOfficerId: fieldOfficerId ?? undefined,
  });
  return result.data || null;
}

export async function fetchAllMeters({ force = false } = {}) {
  const now = Date.now();
  if (!force && metersCache && now - metersCacheAt < METERS_CACHE_MS) {
    return metersCache;
  }
  if (!force && metersInFlight) return metersInFlight;

  metersInFlight = (async () => {
    try {
      const first = await apiFetch('/meters', {
        projectId: PROJECT_ID,
        status: 'pending,accepted,approved,rejected',
        page: 1,
        pageSize: METERS_PAGE_SIZE,
      });
      const firstBatch = first.data || [];
      const totalPages = Math.max(
        1,
        Number(first.meta?.pagination?.totalPages) ||
          Math.ceil((Number(first.meta?.pagination?.total) || firstBatch.length) / METERS_PAGE_SIZE),
      );

      if (totalPages === 1) {
        metersCache = firstBatch;
        metersCacheAt = Date.now();
        return firstBatch;
      }

      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiFetch('/meters', {
            projectId: PROJECT_ID,
            status: 'pending,accepted,approved,rejected',
            page: i + 2,
            pageSize: METERS_PAGE_SIZE,
          }),
        ),
      );

      const all = [
        ...firstBatch,
        ...rest.flatMap((result) => (Array.isArray(result?.data) ? result.data : [])),
      ];

      metersCache = all;
      metersCacheAt = Date.now();
      return all;
    } finally {
      metersInFlight = null;
    }
  })();

  return metersInFlight;
}

export async function fetchMetersByStatus(status, page = 1, pageSize = 100) {
  return apiFetch('/meters', {
    projectId: PROJECT_ID,
    status: metersStatusQuery(status),
    page,
    pageSize,
  });
}

export async function fetchMeterById(meterId, { projectId } = {}) {
  const meterIdNum = Number(meterId);
  if (!Number.isFinite(meterIdNum) || meterIdNum <= 0) {
    throw new Error('A valid meterId is required to fetch meter details.');
  }

  const resolvedProjectIdRaw =
    projectId != null && projectId !== '' ? projectId : PROJECT_ID;
  const resolvedProjectId = Number(resolvedProjectIdRaw);
  if (!Number.isFinite(resolvedProjectId) || resolvedProjectId <= 0) {
    throw new Error('Please select a valid project before fetching meter details.');
  }

  const query = { projectId: resolvedProjectId };

  try {
    const detail = await apiFetch(`/meters/${meterIdNum}`, query);
    const meter = detail?.data ?? null;
    if (meter && Number(meter.id) === meterIdNum) {
      return meter;
    }
  } catch (err) {
    if (err?.status !== 404) {
      // Older deployed API may not expose GET /meters/:id yet — fall back below.
    }
  }

  const list = await apiFetch('/meters', {
    ...query,
    id: meterIdNum,
    page: 1,
    pageSize: 1,
  });
  const rows = Array.isArray(list?.data) ? list.data : [];
  return rows.find((row) => Number(row?.id) === meterIdNum) ?? null;
}


export async function reviewMeterInstallation(
  meterId,
  { decision, comments = '', projectId } = {},
) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const meterIdNum = Number(meterId);
  if (!Number.isFinite(meterIdNum) || meterIdNum <= 0) {
    throw new Error('A valid meterId is required to submit an installation review.');
  }

  const normalizedDecision = String(decision ?? '').trim().toLowerCase();
  if (normalizedDecision !== 'approved' && normalizedDecision !== 'rejected') {
    throw new Error('decision must be "approved" or "rejected".');
  }

  if (normalizedDecision === 'rejected' && !String(comments ?? '').trim()) {
    throw new Error('Please provide a reason for rejection.');
  }

  const resolvedProjectIdRaw =
    projectId != null && projectId !== '' ? projectId : PROJECT_ID;
  const resolvedProjectId = Number(resolvedProjectIdRaw);

  if (!Number.isFinite(resolvedProjectId) || resolvedProjectId <= 0) {
    console.error('[installation-review] projectId is missing or invalid', {
      provided: projectId,
      envFallback: PROJECT_ID,
    });
    throw new Error(
      'Please select a project before approving or rejecting this meter.',
    );
  }

  const projectQs = `projectId=${encodeURIComponent(String(resolvedProjectId))}`;
  const url = `${BASE_URL}/meters/${encodeURIComponent(String(meterIdNum))}/installation-review?${projectQs}`;

  const body = {
    decision: normalizedDecision,
    comments: comments == null ? '' : String(comments),
    projectId: resolvedProjectId,
  };

  console.info('[installation-review] PATCH', {
    url,
    meterId: meterIdNum,
    projectId: resolvedProjectId,
    body,
    hasAuthBearer: Boolean(token),
    apiBaseEffective: BASE_URL,
  });

  let response;
  try {
    response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    console.error('[installation-review] network error (fetch failed)', {
      message: networkErr?.message,
      name: networkErr?.name,
      url,
      cause: networkErr?.cause,
    });
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  const responseText = await response.text();

  /** @type {Record<string, unknown>} */
  let result = {};
  if (responseText) {
    try {
      result = JSON.parse(responseText);
    } catch {
      console.warn('[installation-review] response is not JSON', {
        status: response.status,
        snippet: responseText.slice(0, 500),
      });
      result = { _parseError: true, rawBodySnippet: responseText.slice(0, 500) };
    }
  }

  console.info('[installation-review] response', {
    status: response.status,
    ok: response.ok,
    bodyKeys: typeof result === 'object' && result ? Object.keys(result) : [],
    result:
      typeof result === 'object' && result && Object.keys(result).length > 0
        ? result
        : '(empty)',
  });

  if (response.status === 401 || response.status === 403) {
    console.warn('[installation-review] unauthorized — clearing session');
    sessionStorage.removeItem('bi_token');
    sessionStorage.removeItem('bi_user');
    window.location.href = '/login';
    throw new ApiError(SESSION_EXPIRED_MESSAGE, { status: response.status });
  }

  if (!response.ok) {
    const status = response.status;
    console.error('[installation-review] request failed', {
      status,
      statusText: response.statusText,
      fullParsedBody: result,
    });
    if (status === 408 || status === 504) {
      throw new ApiError(TIMEOUT_ERROR_MESSAGE, { status });
    }
    if (status >= 500) {
      throw new ApiError(SERVER_ERROR_MESSAGE, { status });
    }
    const message = getApiErrorMessage(
      result,
      'Could not submit the review. Please try again.',
    );
    throw new ApiError(message, { status });
  }

  invalidateMetersCache();
  return result;
}

export async function patchMeterLocationDetails(
  meterId,
  { projectId, qtr, state, depot, area } = {},
) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const meterIdNum = Number(meterId);
  if (!Number.isFinite(meterIdNum) || meterIdNum <= 0) {
    throw new Error('A valid meterId is required to update meter location details.');
  }

  const resolvedProjectIdRaw =
    projectId != null && projectId !== '' ? projectId : PROJECT_ID;
  const resolvedProjectId = Number(resolvedProjectIdRaw);
  if (!Number.isFinite(resolvedProjectId) || resolvedProjectId <= 0) {
    throw new Error('Please select a valid project before updating meter location details.');
  }

  const body = { projectId: resolvedProjectId };
  if (qtr != null) body.qtr = String(qtr);
  if (state != null) body.state = String(state);
  if (depot != null) body.depot = String(depot);
  if (area != null) body.area = String(area);

  let response;
  try {
    response = await fetch(
      `${BASE_URL}/meters/${encodeURIComponent(String(meterIdNum))}/location-details`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      },
    );
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to update meter location details. Please try again.');
  }
  invalidateMetersCache();
  return response.json().catch(() => ({}));
}

export async function patchMeterOldMeterDetails(
  meterId,
  { projectId, oldMeterNumber, oldMeterReading } = {},
) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const meterIdNum = Number(meterId);
  if (!Number.isFinite(meterIdNum) || meterIdNum <= 0) {
    throw new Error('A valid meterId is required to update old meter details.');
  }

  const resolvedProjectIdRaw =
    projectId != null && projectId !== '' ? projectId : PROJECT_ID;
  const resolvedProjectId = Number(resolvedProjectIdRaw);
  if (!Number.isFinite(resolvedProjectId) || resolvedProjectId <= 0) {
    throw new Error('Please select a valid project before updating old meter details.');
  }

  const body = { projectId: resolvedProjectId };
  if (oldMeterNumber != null) body.oldMeterNumber = String(oldMeterNumber);
  if (oldMeterReading != null) body.oldMeterReading = Number(oldMeterReading);

  let response;
  try {
    response = await fetch(
      `${BASE_URL}/meters/${encodeURIComponent(String(meterIdNum))}/old-meter-details`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      },
    );
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to update old meter details. Please try again.');
  }
  invalidateMetersCache();
  return response.json().catch(() => ({}));
}

export function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

export function normalizeStatus(raw) {
  if (!raw) return 'Unknown';
  const s = String(raw).toUpperCase().trim();
  if (s === 'ACCEPTED' || s === 'APPROVED') return 'Accepted';
  if (
    s === 'PENDING' ||
    s === 'PENDING_VERIFICATION' ||
    s === 'PENDING_TO_VERIFY' ||
    s === 'PENDING TO VERIFY'
  ) {
    return 'Pending';
  }
  if (s === 'REJECTED') return 'Rejected';
  return raw;
}

function getMeterPhotoPaths(meter) {
  const directList = Array.isArray(meter?.photoPaths) ? meter.photoPaths : [];
  const altList = Array.isArray(meter?.photos) ? meter.photos : [];
  const nestedList = Array.isArray(meter?.images) ? meter.images : [];
  const singleCandidates = [
    meter?.photoPath,
    meter?.image,
    meter?.imageUrl,
    meter?.photo1,
    meter?.photo2,
    meter?.photo3,
    meter?.photo4,
    meter?.photo5,
    meter?.photo6,
    meter?.photo7,
    meter?.photo8,
  ];

  const mixed = [...directList, ...altList, ...nestedList, ...singleCandidates];
  const asStringUrl = mixed
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        return item.url || item.path || item.imageUrl || item.photoPath || '';
      }
      return '';
    })
    .filter(Boolean);

  return [...new Set(asStringUrl)];
}

export function normalizeMeter(m, officers = {}) {
  const rawUserId = m.userId ?? m.user_id ?? null;
  const userIdNum = rawUserId != null && rawUserId !== '' ? Number(rawUserId) : NaN;
  const userId = Number.isFinite(userIdNum) && userIdNum > 0 ? userIdNum : null;
  const projectIdRaw = m?.projectId ?? m?.project_id ?? m?.project?.id ?? null;
  const projectIdNum = projectIdRaw != null ? Number(projectIdRaw) : null;
  const locationIdRaw = m?.locationId ?? m?.location_id ?? m?.location?.id ?? null;
  const locationIdNum = locationIdRaw != null ? Number(locationIdRaw) : null;
  return {
    id: m.id,
    projectId: Number.isFinite(projectIdNum) ? projectIdNum : null,
    locationId: Number.isFinite(locationIdNum) ? locationIdNum : null,
    meter: m.meterNumber || '—',
    qtr: m.location?.qtrno || '—',
    qtrLocationId: m.location?.qtrno_id ?? null,
    area: m.location?.area || '—',
    areaLocationId: m.location?.area_id ?? null,
    depot: m.location?.depot || '—',
    depotLocationId: m.location?.depot_id ?? null,
    state: m.location?.state || '—',
    stateLocationId: m.location?.state_id ?? null,
    type: m.type || '—',
    phase: m.phase || '—',
    date: formatDate(m.installationDate),
    dateRaw: m.installationDate || '',
    createdAtRaw: m.createdAt || '',
    updatedAtRaw: m.updatedAt || '',
    status: normalizeStatus(m.status),
    communicationStatus: m.communicationStatus || m.communication_status || '',
    statusRaw: m.status,
    officer: userId ? getOfficerName(officers, userId) : '—',
    userId,
    isReplacement: m.isReplacement ?? m.is_replacement ?? false,
    oldMeterNumber: m.oldMeterNumber ?? m.old_meter_number ?? null,
    oldMeterReading: m.oldMeterReading ?? m.old_meter_reading ?? null,
    latitude: m.latitude,
    longitude: m.longitude,
    photoPaths: getMeterPhotoPaths(m),
  };
}

export function clearOfficerCache() {
  cachedOfficers = null;
  cachedFieldOfficerRoleId = null;
  invalidateMetersCache();
}



const TICKET_STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];

const TICKET_ISSUE_TYPES = [
  'Voltage fluctuation',
  'Meter not working',
  'Wrong reading',
  'Connection request',
  'Power outage',
  'Meter installation',
  'Display flicker',
  'No communication',
  'Seal damaged',
  'Tampering alert',
];

const TICKET_CATEGORIES = [
  'Connection Issue',
  'Meter Issue',
  'Billing',
  'New Connection',
  'Outage',
  'Installation',
];

const TICKET_LOCATIONS = [
  'Bengaluru North',
  'Chennai Central',
  'Hyderabad West',
  'Mumbai East',
  'Pune South',
  'Secunderabad',
];

const TICKET_OFFICERS = [
  'Sandeep Kulkarni',
  'Priya Nair',
  'Rahul Verma',
  'Meera Joshi',
  'Vikram Singh',
  'Anjali Mehta',
  'Arjun Patel',
  'Kavya Reddy',
  'Manoj Iyer',
  'Sneha Kapoor',
];

const TICKET_TEAMS = ['BI-Tech Team', 'Field Ops Team', 'Network Team', 'Customer Care'];

const TICKET_TITLE_BY_CATEGORY = {
  'Connection Issue': 'Meter Connection Issue',
  'Meter Issue': 'Meter Fault Report',
  Billing: 'Billing Dispute',
  'New Connection': 'New Connection Request',
  Outage: 'Power Outage Report',
  Installation: 'Installation Follow-up',
};

const TICKET_PRIORITIES = ['High', 'Medium', 'Low'];

const TICKET_CIRCLES = ['South Circle', 'North Circle', 'East Circle', 'West Circle'];
const TICKET_DIVISIONS = ['Division A', 'Division B', 'Division C', 'Division D'];
const TICKET_SUB_DIVISIONS = ['Sub-Div 1', 'Sub-Div 2', 'Sub-Div 3'];
const TICKET_SECTIONS = ['Section A', 'Section B', 'Section C'];

/** Deterministic pseudo-random so the mock dataset is stable between renders. */
function seeded(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const MOCK_TICKETS = (() => {
  const rng = seeded(42);
  const pick = (arr) => arr[Math.floor(rng() * arr.length)];
  const STATUS_DIST = ['Open', 'Open', 'In Progress', 'In Progress', 'Resolved', 'Closed'];
  const minutesPer = 7;
  const baseDate = new Date('2025-01-18T16:24:00Z');

  const tickets = Array.from({ length: 72 }).map((_, i) => {
    const id = `TKT-${2891 - i}`;
    const status = pick(STATUS_DIST);
    const raisedAt = new Date(baseDate.getTime() - i * minutesPer * 60 * 1000).toISOString();
    const category = pick(TICKET_CATEGORIES);
    const location = pick(TICKET_LOCATIONS);
    const section = pick(TICKET_SECTIONS);
    const fieldOfficer = pick(TICKET_OFFICERS);
    const issueType = pick(TICKET_ISSUE_TYPES);
    const assignedTo = pick(TICKET_TEAMS);
    const phoneMid = String(230 + (i % 70)).padStart(3, '0');
    const phoneEnd = String(10000 + ((i * 17) % 90000)).padStart(5, '0');
    const fieldOfficerPhone = `+91 98${phoneMid} ${phoneEnd}`;
    const priority = TICKET_PRIORITIES[i % TICKET_PRIORITIES.length];
    const title =
      TICKET_TITLE_BY_CATEGORY[category] || `${category} — ${issueType}`;
    const description =
      category === 'Connection Issue' && issueType.toLowerCase().includes('voltage')
        ? "Hello! I'm here to help with your Meter Connection Issue. The voltage is unstable and has been fluctuating for the past week."
        : "Hello! I'm here to help with your Meter Connection Issue. The voltage keeps dropping and rising unpredictably for the last week.";
   
   
    const updatedAt = new Date(
      new Date(raisedAt).getTime() + ((i % 48) + 1) * 60 * 60 * 1000,
    ).toISOString();
    return {
      id,
      title,
      priority,
      fieldOfficer,
      fieldOfficerPhone,
      issueType,
      category,
      location,
      locationLabel: `${location} - Sec ${(i % 14) + 1}`,
      circle: pick(TICKET_CIRCLES),
      division: pick(TICKET_DIVISIONS),
      subDivision: pick(TICKET_SUB_DIVISIONS),
      section,
      raisedAt,
      createdAt: raisedAt,
      updatedAt,
      status,
      assignedTo,
      meterNumber: `MTR-${4500 + i}`,
      description,
      comments: [],
      attachments: [
        { id: `${id}-a1`, name: 'Meter-photo.jpg', sizeBytes: 2516582 },
        { id: `${id}-a2`, name: 'meter-reading.pdf', sizeBytes: 190464 },
      ],
    };
  });

  /** Dummy row: timeline shows “Under Investigation” as in progress (orange step). */
  const demoRaised = new Date('2025-01-20T10:00:00Z').toISOString();
  tickets.unshift({
    id: 'TKT-DEMO-INVESTIGATION',
    title: 'Meter Connection Issue',
    priority: 'Medium',
    fieldOfficer: 'Sandeep Kulkarni',
    fieldOfficerPhone: '+91 98230 45100',
    issueType: 'Voltage fluctuation',
    category: 'Connection Issue',
    location: 'Bengaluru North',
    locationLabel: 'Bengaluru North - Sec 2',
    circle: 'North Circle',
    division: 'Division A',
    subDivision: 'Sub-Div 1',
    section: 'Section A',
    raisedAt: demoRaised,
    createdAt: demoRaised,
    updatedAt: new Date().toISOString(),
    status: 'In Progress',
    assignedTo: 'Field Ops Team',
    meterNumber: 'MTR-9012',
    description:
      "Demo ticket: status is In Progress so the timeline shows 'Under Investigation' with the in-progress state.",
    comments: [],
    attachments: [
      { id: 'TKT-DEMO-INVESTIGATION-a1', name: 'site-photo.jpg', sizeBytes: 1024000 },
      { id: 'TKT-DEMO-INVESTIGATION-a2', name: 'meter-reading.pdf', sizeBytes: 190464 },
      { id: 'TKT-DEMO-INVESTIGATION-a3', name: 'voltage-log.csv', sizeBytes: 8192 },
    ],
  });

  return tickets;
})();

/** Static numbers chosen to match the design screenshot. */
const TICKET_KPIS = {
  total: 5800,
  open: 312,
  inProgress: 184,
  resolved: 921,
  avgResolutionDays: 2.4,
};

const TICKET_MONTHLY_REPORT = {
  Year: {
    labels: [
      'January', 'February', 'March', 'April', 'May', 'Jun',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    raised: [6800, 7000, 6900, 6850, 6950, 6800, 6900, 6950, 6850, 6900, 6800, 6950],
    resolved: [5600, 5800, 5650, 5700, 5650, 5600, 5700, 5750, 5650, 5700, 5600, 5750],
  },
  Month: {
    labels: Array.from({ length: 30 }, (_, i) => String(i + 1).padStart(2, '0')),
    raised: Array.from({ length: 30 }, (_, i) => 220 + ((i * 17) % 60)),
    resolved: Array.from({ length: 30 }, (_, i) => 180 + ((i * 13) % 50)),
  },
  Week: {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    raised: [820, 910, 880, 940, 870, 760, 690],
    resolved: [690, 740, 720, 780, 700, 620, 560],
  },
};

function clone(value) {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

export function getTicketStatuses() {
  return [...TICKET_STATUSES];
}

export function getTicketTeams() {
  return [...TICKET_TEAMS];
}

export function getTicketPriorities() {
  return [...TICKET_PRIORITIES];
}

export function getTicketIssueTypes() {
  return [...TICKET_ISSUE_TYPES];
}

export function getTicketCategories() {
  return [...TICKET_CATEGORIES];
}

export function getTicketLocations() {
  return [...TICKET_LOCATIONS];
}

export function getTicketCircles() {
  return [...TICKET_CIRCLES];
}

export function getTicketDivisions() {
  return [...TICKET_DIVISIONS];
}

export function getTicketSubDivisions() {
  return [...TICKET_SUB_DIVISIONS];
}

export function getTicketSections() {
  return [...TICKET_SECTIONS];
}

export function getTicketKpis() {
  return { ...TICKET_KPIS };
}

export function getTicketMonthlyReport(range = 'Year') {
  const r = TICKET_MONTHLY_REPORT[range] ? range : 'Year';
  return clone(TICKET_MONTHLY_REPORT[r]);
}

export async function fetchTickets() {
  return clone(MOCK_TICKETS);
}

export async function fetchTicketMonthlyReport(range = 'Year') {
  const result = await apiFetch('/tickets/monthly-report', { range });
  return result.data || null;
}

export async function fetchTicketById(ticketId) {
  const match = MOCK_TICKETS.find((t) => t.id === ticketId);
  if (!match) throw new Error(`Ticket ${ticketId} not found`);
  return clone(match);
}

export async function addTicketComment(ticketId, comment) {
  const target = MOCK_TICKETS.find((t) => t.id === ticketId);
  if (!target) throw new Error(`Ticket ${ticketId} not found`);
  const entry = {
    id: `c${(target.comments?.length || 0) + 1}-${Date.now()}`,
    author: comment.author || 'Admin',
    role: comment.role || 'Admin',
    message: String(comment.message || '').trim(),
    createdAt: new Date().toISOString(),
  };
  if (!entry.message) throw new Error('Comment cannot be empty');
  target.comments = [...(target.comments || []), entry];
  target.updatedAt = entry.createdAt;
  return clone(target);
}

export async function updateTicketStatus(ticketId, nextStatus) {
  if (!TICKET_STATUSES.includes(nextStatus)) {
    throw new Error(`Invalid status: ${nextStatus}`);
  }
  const target = MOCK_TICKETS.find((t) => t.id === ticketId);
  if (!target) throw new Error(`Ticket ${ticketId} not found`);
  target.status = nextStatus;
  target.updatedAt = new Date().toISOString();
  return clone(target);
}

/**
 * Update ticket fields (mock). Prefer this for the details screen "Save update" flow.
 */
export async function updateTicket(ticketId, patch = {}) {
  const target = MOCK_TICKETS.find((t) => t.id === ticketId);
  if (!target) throw new Error(`Ticket ${ticketId} not found`);
  if (patch.status != null) {
    if (!TICKET_STATUSES.includes(patch.status)) {
      throw new Error(`Invalid status: ${patch.status}`);
    }
    target.status = patch.status;
  }
  if (patch.assignedTo != null) {
    if (!TICKET_TEAMS.includes(patch.assignedTo)) {
      throw new Error(`Invalid assignee: ${patch.assignedTo}`);
    }
    target.assignedTo = patch.assignedTo;
  }
  if (patch.priority != null) {
    if (!TICKET_PRIORITIES.includes(patch.priority)) {
      throw new Error(`Invalid priority: ${patch.priority}`);
    }
    target.priority = patch.priority;
  }
  target.updatedAt = new Date().toISOString();
  return clone(target);
}

function extractLocationList(result) {
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.locations)) return result.locations;
  if (Array.isArray(result)) return result;
  return [];
}

function extractLocationTypeList(result) {
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result)) return result;
  return [];
}

export async function fetchLocationTypes(projectId, { dropdown = true } = {}) {
  const result = await apiFetch('/location-types', {
    projectId,
    dropdown: dropdown ? 'true' : 'false',
    isActive: 'true',
    limit: 100,
    page: 1,
  });
  return extractLocationTypeList(result);
}

const LOCATIONS_PAGE_SIZE = 500;

function isActiveLocationRow(row) {
  const v = row?.is_active ?? row?.isActive ?? row?.active;
  if (v === false || v === 0 || v === '0') return false;
  if (typeof v === 'string' && v.toLowerCase() === 'false') return false;
  return true;
}

export function buildProjectLocationQuery(projectId) {
  const pid = Number(projectId);
  if (!Number.isInteger(pid) || pid <= 0) return {};
  return { projectId: pid, project_id: pid };
}

export async function fetchAllLocations(queryParams = {}) {
  const limit = Number(queryParams.limit) > 0 ? Number(queryParams.limit) : LOCATIONS_PAGE_SIZE;
  let page = 1;
  const all = [];

  while (true) {
    const result = await apiFetch('/locations', {
      ...queryParams,
      page,
      limit,
    });
    const batch = extractLocationList(result);
    all.push(...batch);
    if (batch.length === 0 || batch.length < limit) break;
    page += 1;
  }

  return all;
}

/**
 * Loads locations for hierarchy/table. When projectId is set, scopes API to that project (much faster).
 * Uses a single paginated "all" fetch and derives active rows client-side when scoped.
 */
export async function fetchAllLocationsForHierarchy(projectId) {
  const query = buildProjectLocationQuery(projectId);

  if (query.projectId) {
    const all = await fetchAllLocations({ ...query, isActive: 'all' });
    const active = all.filter(isActiveLocationRow);
    return { active, all };
  }

  const [active, all] = await Promise.all([
    fetchAllLocations({ isActive: 'true' }),
    fetchAllLocations({ isActive: 'all' }),
  ]);
  return { active, all };
}

/**
 * GET /locations?dropdown=true — parent options for create form (by project + parent type).
 * @param {number|string} projectId
 * @param {number|string} typeId location_types.id of the parent level
 */
export async function fetchLocationsDropdown(projectId, typeId) {
  const result = await apiFetch('/locations', {
    projectId,
    typeId,
    dropdown: 'true',
    isActive: 'true',
  });
  return extractLocationList(result);
}

export async function createLocation(payload) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${BASE_URL}/locations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to create location. Please try again.');
  }
  return response.json().catch(() => ({}));
}

export async function updateLocation(id, payload) {
  const locationId = Number(id);
  if (!Number.isInteger(locationId) || locationId <= 0) {
    throw new Error('A valid location id is required for update.');
  }

  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${BASE_URL}/locations/${encodeURIComponent(String(locationId))}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);
  if (!response.ok) {
    await throwForResponse(response, 'Failed to update location. Please try again.');
  }
  return response.json().catch(() => ({}));
}

/**
 * POST /locations/upload?projectId=… — bulk import locations from a file (multipart).
 * Expects the server to accept a multipart field named `file` (Excel `.xlsx` / `.xls` per API).
 * @param {number|string} projectId
 * @param {File} file
 */
export async function uploadLocationsBulk(projectId, file) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const formData = new FormData();
  formData.append('file', file);

  const url = `${BASE_URL}/locations/upload?projectId=${encodeURIComponent(String(projectId))}`;
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
  } catch (networkErr) {
    throw new ApiError(NETWORK_ERROR_MESSAGE, { status: 0, original: networkErr });
  }

  handleAuthExpiry(response);

  const text = await response.text();
  let parsed = {};
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { message: text };
    }
  }

  if (!response.ok) {
    const status = response.status;
    if (status === 408 || status === 504) {
      throw new ApiError(TIMEOUT_ERROR_MESSAGE, { status });
    }
    if (status >= 500) {
      throw new ApiError(SERVER_ERROR_MESSAGE, { status });
    }
    throw new ApiError(
      getApiErrorMessage(parsed, 'Upload failed. Please check the file and try again.'),
      { status },
    );
  }

  return parsed;
}
